import React, {
    useCallback,
    useContext,
    useState,
    useRef
} from 'react';

import { renderContentFromMetadata } from '@jutro/uiconfig';
import {
    SgAddDriver,
    SgRemoveDriver,
    SgDriversSummary,
    CepConstants
} from 'saga-cep-specific-components';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import SgIdGenerator from 'sg-id-generator';
import SpmConstants from 'sg-spm-constants';
import SgCepSideMenu from 'sg-cep-side-menu';
import { EndorsementService } from 'gw-capability-policychange';
import { useSgTrackPageName } from 'saga-modules';
import { WizardPage } from 'gw-portals-wizard-react';
import _ from 'lodash';
import { Loader } from '@jutro/components';
import metadata from './SgEndorsementDriverPage.metadata.json5';
import './SgEndorsementDriverPage.messages';

function SgEndorsementDriverPage(props) {
    const {
        wizardData: modelVM,
        updateWizardData: updateData,
        sgWizardPropsExt,
        goNext,
    } = props;

    const {
        handleCancel,
        handleGoBack
    } = sgWizardPropsExt;

    const [isLoading, setLoadingState] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const initialPolicyChangeData = useRef(_.cloneDeep(modelVM.value));

    const [isAddingDriver, setIsAddingDriver] = useState(false);
    const [addingDriverIndex, setAddingDriverIndex] = useState(0);
    const [isRemovingDriver, setIsRemovingDriver] = useState(false);

    useSgTrackPageName(CepConstants.PAGE_NAMES.DRIVER_CHANGES);

    const restoreModel = useCallback(() => {
        modelVM.value = _.cloneDeep(initialPolicyChangeData.current);
        updateData(modelVM);
    }, [initialPolicyChangeData, modelVM]);

    const addNewDriver = useCallback(() => {
        const viewModelContext = {};
        const newDriverModel = viewModelService.create(
            {
                person: {},
                dateOfBirth: {},
                convictions: [],
                claims: [],
                tempID: `driver${SgIdGenerator.getNextId()}`,
                isPolicyHolder: false
            },
            'pc',
            'edgev11.capabilities.policyjob.lob.privatemotor.coverables.dto.SPMDriverDTO',
            viewModelContext
        );
        const policyChangeCopy = _.clone(modelVM);
        policyChangeCopy.lobData.privateMotor.coverables.spmDrivers.value.push(newDriverModel.value);
        updateData(policyChangeCopy);
    }, [modelVM]);

    const onAddDriver = useCallback(() => {
        setIsRemovingDriver(false);
        addNewDriver();
        setAddingDriverIndex(modelVM.lobData.privateMotor.coverables.spmDrivers.length - 1);
        setIsAddingDriver(true);
    }, [_.get(modelVM, 'lobData.privateMotor.coverables.spmDrivers.value'), setIsRemovingDriver]);

    const onRemoveDriver = useCallback(() => {
        if (isAddingDriver) {
            restoreModel();
            setIsAddingDriver(false);
            setAddingDriverIndex(0);
        }
        setIsRemovingDriver(true);
    }, [isAddingDriver]);

    const commonProps = {
        modelVM,
        updateData
    };

    const onContinue = useCallback(() => {
        setLoadingState(true);
        EndorsementService.saveEndorsement(modelVM.value)
            .then((response) => {
                const policyChangeValueCopy = _.cloneDeep(modelVM.value);
                _.merge(policyChangeValueCopy, response);
                modelVM.value = policyChangeValueCopy;
                updateData(modelVM);
                goNext(modelVM);
            }).finally(() => {
                setLoadingState(false);
            });
    }, [goNext, modelVM, updateData]);

    const overrideProps = {
        cepEndorsementDriverSummary: {
            ...commonProps
        },
        cepEndorsementAddDriver: {
            visible: isAddingDriver,
            addingDriverIndex,
            onContinue,
            handleCancel,
            handleGoBack,
            ...commonProps
        },
        cepEndorsementRemoveDriver: {
            visible: isRemovingDriver,
            onContinue,
            handleCancel,
            handleGoBack,
            ...commonProps
        },
        driverChangesAddButton: {
            disabled: _.get(modelVM, 'lobData.privateMotor.coverables.spmDrivers.value.length') >= SpmConstants.MAX_DRIVERS_NUMBER || isAddingDriver
        },
        driverChangesRemoveButton: {
            visible: _.get(modelVM, 'lobData.privateMotor.coverables.spmDrivers.value.length') >= 3
                || (_.get(modelVM, 'lobData.privateMotor.coverables.spmDrivers.value.length') === 2 && !isAddingDriver),
            disabled: _.get(modelVM, 'lobData.privateMotor.coverables.spmDrivers.value.length') === 1 || isRemovingDriver
        },
        goBackButton: {
            visible: !isAddingDriver && !isRemovingDriver
        },
        backToMyAccountLinkContainer: {
            visible: !isAddingDriver && !isRemovingDriver
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onAddDriver,
            onRemoveDriver,
            handleCancel,
            handleGoBack
        },
        resolveComponentMap: {
            SgAddDriver,
            SgRemoveDriver,
            SgDriversSummary,
            SgCepSideMenu
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }
    return (
        <WizardPage showPrevious={false} showNext={false}>
            {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
        </WizardPage>
    );
}

export default SgEndorsementDriverPage;
